import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui';

interface SignatureCanvasProps {
  width?: number;
  height?: number;
  onChange: (svgPath: string) => void;
}

/**
 * Canvas HTML5 puro pra capturar assinatura digital (R5 PR técnico).
 *
 * Implementação minimal sem deps:
 * - Captura pointer events (mouse + touch via Pointer Events API)
 * - Acumula paths SVG em estado local
 * - Botão "Limpar" reseta tudo
 * - Botão "Salvar" chama onChange com SVG path data completo
 *
 * Decisão arquitetural (handoff): canvas HTML5 puro evita dep externa
 * (~50 linhas vs ~200KB react-signature-canvas). Trade-off: features
 * tipo undo/redo ficam pra futuro.
 *
 * Pra render final no backend, o SVG é embedded em <svg viewBox="0 0 W H">
 * com os paths concatenados.
 */
export function SignatureCanvas({
  width = 400,
  height = 150,
  onChange,
}: SignatureCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const currentPathRef = useRef<string>('');
  const [paths, setPaths] = useState<string[]>([]);
  const [isEmpty, setIsEmpty] = useState(true);

  // Redesenha canvas quando paths mudam
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const path of paths) {
      const path2d = new Path2D(path);
      ctx.stroke(path2d);
    }
    if (currentPathRef.current) {
      const path2d = new Path2D(currentPathRef.current);
      ctx.stroke(path2d);
    }
  }, [paths]);

  function getRelativePos(e: React.PointerEvent<HTMLCanvasElement>) {
    const rect = canvasRef.current!.getBoundingClientRect();
    const scaleX = canvasRef.current!.width / rect.width;
    const scaleY = canvasRef.current!.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }

  function handlePointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    e.preventDefault();
    canvasRef.current!.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const { x, y } = getRelativePos(e);
    currentPathRef.current = `M ${x.toFixed(1)} ${y.toFixed(1)}`;
    setIsEmpty(false);
  }

  function handlePointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) return;
    const { x, y } = getRelativePos(e);
    currentPathRef.current += ` L ${x.toFixed(1)} ${y.toFixed(1)}`;
    // Force re-render
    setPaths((p) => [...p]);
  }

  function handlePointerUp(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    canvasRef.current?.releasePointerCapture(e.pointerId);
    if (currentPathRef.current) {
      setPaths((p) => [...p, currentPathRef.current]);
      currentPathRef.current = '';
    }
  }

  function handleClear() {
    setPaths([]);
    currentPathRef.current = '';
    setIsEmpty(true);
    onChange('');
  }

  function handleSave() {
    const allPaths = paths.join(' ');
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}"><path d="${allPaths}" stroke="#1a1a1a" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    onChange(svg);
  }

  return (
    <div className="space-y-2">
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        className="border border-line dark:border-line-dark rounded-lg bg-white touch-none w-full"
        style={{ touchAction: 'none', maxWidth: width }}
        data-testid="signature-canvas"
      />
      <div className="flex gap-2">
        <Button type="button" size="sm" variant="ghost" onClick={handleClear}>
          Limpar
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={isEmpty}
          data-testid="signature-save"
        >
          Confirmar assinatura
        </Button>
      </div>
    </div>
  );
}
